import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Recent Universal-Search history.
 *
 * Mirrors `recentPatientSearchesStore` but for the global Cmd/Ctrl+K palette
 * (orders + patients + subscriptions). Surfaced at the top of the palette when
 * the search field is empty, so staff can re-open something they just looked at
 * without retyping the order number / patient name.
 *
 * Storage: `localStorage` via Zustand `persist`. Per-browser, survives refresh
 * and logout — same trade-off as patient-search history.
 */

export const RECENT_GLOBAL_SEARCHES_CAP = 12;

export type GlobalSearchEntryType = 'order' | 'patient' | 'subscription';

interface BaseEntry {
  /** Stable composite key — `${type}:${id}` — used for dedupe. */
  key: string;
  type: GlobalSearchEntryType;
  /** The id used to navigate (order.id, patient.compositeId, subscription.id). */
  id: string;
  pharmacyId?: string;
  pharmacyName?: string;
  /** ISO timestamp of the most recent view. */
  viewedAt: string;
}

export interface RecentOrderEntry extends BaseEntry {
  type: 'order';
  orderNumber: string;
  patientName?: string;
  rxNumber?: string;
  workflowStage?: string;
}

export interface RecentPatientEntry extends BaseEntry {
  type: 'patient';
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
}

export interface RecentSubscriptionEntry extends BaseEntry {
  type: 'subscription';
  rxNumber?: string;
  medicationName?: string;
  patientName?: string;
}

export type RecentGlobalSearchEntry =
  | RecentOrderEntry
  | RecentPatientEntry
  | RecentSubscriptionEntry;

/**
 * Distributive Omit — without this, `Omit<RecentGlobalSearchEntry, 'viewedAt'>`
 * collapses the discriminated union and the type-checker rejects variant-only
 * properties like `orderNumber` / `firstName` / `rxNumber` at call sites.
 */
type DistributiveOmit<T, K extends keyof any> = T extends unknown ? Omit<T, K> : never;
export type RecentGlobalSearchEntryInput = DistributiveOmit<RecentGlobalSearchEntry, 'viewedAt'>;

interface RecentGlobalSearchesState {
  entries: RecentGlobalSearchEntry[];
  /** Add or refresh an entry. Dedupes by `key`, moves to top, trims to cap. */
  record: (entry: RecentGlobalSearchEntryInput) => void;
  /** Clear all history. */
  clear: () => void;
}

export const useRecentGlobalSearchesStore = create<RecentGlobalSearchesState>()(
  devtools(
    persist(
      (set) => ({
        entries: [],
        record: (entry) =>
          set((state) => {
            const now = new Date().toISOString();
            const filtered = state.entries.filter((e) => e.key !== entry.key);
            const next = [
              { ...entry, viewedAt: now } as RecentGlobalSearchEntry,
              ...filtered,
            ].slice(0, RECENT_GLOBAL_SEARCHES_CAP);
            return { entries: next };
          }),
        clear: () => set({ entries: [] }),
      }),
      {
        name: 'recent-global-searches',
        version: 1,
      },
    ),
    { name: 'RecentGlobalSearches' },
  ),
);
